import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Star,
  StarBorder,
  Person,
  CalendarToday,
  LocationOn,
  Email,
  Phone,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { formatDateForDisplay } from '../utils/dateUtils';

interface ProviderRatingDialogProps {
  open: boolean;
  onClose: () => void;
  provider: {
    _id: string;
    businessName?: string;
    firstName: string;
    lastName: string;
    rating?: number;
  };
}

interface Review {
  _id: string;
  rating: number;
  title?: string;
  comment: string;
  createdAt: string;
  customerId: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
  };
  serviceRequestId: {
    serviceType: {
      name: string;
    };
    address: {
      street: string;
      city: string;
      state: string;
    };
    completedDate: string;
  };
}

interface RatingStats {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

const ProviderRatingDialog: React.FC<ProviderRatingDialogProps> = ({
  open,
  onClose,
  provider,
}) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && provider && provider._id) {
      fetchReviews();
    }
  }, [open, provider]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get(`/reviews/provider/${provider._id}`);
      
      if (response.success && response.data) {
        setReviews((response.data as any).reviews || []);
        setStats((response.data as any).stats);
      } else {
        setError('Failed to load reviews');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number, size: 'small' | 'medium' | 'large' = 'medium') => {
    const stars = [];
    const starSize = size === 'small' ? 16 : size === 'large' ? 32 : 24;
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        i <= rating ? (
          <Star key={i} sx={{ fontSize: starSize, color: '#ffc107' }} />
        ) : (
          <StarBorder key={i} sx={{ fontSize: starSize, color: '#e0e0e0' }} />
        )
      );
    }
    return <Box sx={{ display: 'flex', alignItems: 'center' }}>{stars}</Box>;
  };

  const formatDate = (dateString: string) => {
    // Use timezone-aware date formatting to avoid timezone shifts
    return formatDateForDisplay(dateString);
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return 'success';
    if (rating >= 3.5) return 'primary';
    if (rating >= 2.5) return 'warning';
    return 'error';
  };

  if (!provider) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Person />
          <Box>
            <Typography variant="h6">
              {provider.businessName || `${provider.firstName} ${provider.lastName}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Service Provider Reviews
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box>
            {/* Rating Summary */}
            {stats && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="primary" gutterBottom>
                          {stats.averageRating.toFixed(1)}
                        </Typography>
                        {renderStars(Math.round(stats.averageRating), 'large')}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Based on {stats.totalReviews} review{stats.totalReviews !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Typography variant="h6" gutterBottom>
                        Rating Distribution
                      </Typography>
                      {[5, 4, 3, 2, 1].map((rating) => {
                        const count = stats.ratingDistribution[rating as keyof typeof stats.ratingDistribution];
                        const percentage = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;
                        
                        return (
                          <Box key={rating} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: 20 }}>
                              {rating}
                            </Typography>
                            <Star sx={{ fontSize: 16, color: '#ffc107', mx: 1 }} />
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{
                                flexGrow: 1,
                                mx: 2,
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: '#ffc107',
                                },
                              }}
                            />
                            <Typography variant="body2" sx={{ minWidth: 30, textAlign: 'right' }}>
                              {count}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Reviews List */}
            <Typography variant="h6" gutterBottom>
              Customer Reviews
            </Typography>
            
            {reviews.length === 0 ? (
              <Alert severity="info">
                No reviews yet. Be the first to review this provider!
              </Alert>
            ) : (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {reviews.map((review) => (
                  <Card key={review._id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {review.customerId.firstName} {review.customerId.lastName}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            {renderStars(review.rating, 'small')}
                            <Chip
                              label={`${review.rating}/5`}
                              size="small"
                              color={getRatingColor(review.rating) as any}
                            />
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(review.createdAt)}
                        </Typography>
                      </Box>

                      {review.title && (
                        <Typography variant="subtitle2" gutterBottom>
                          {review.title}
                        </Typography>
                      )}

                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {review.comment}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 2 }}>
                        <LocationOn sx={{ fontSize: 16 }} />
                        <Typography variant="caption">
                          {review.serviceRequestId.address.street}, {review.serviceRequestId.address.city}, {review.serviceRequestId.address.state}
                        </Typography>
                        <CalendarToday sx={{ fontSize: 16, ml: 2 }} />
                        <Typography variant="caption">
                          Service completed: {formatDate(review.serviceRequestId.completedDate)}
                        </Typography>
                      </Box>

                      {/* Customer Contact Information */}
                      <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 1 }}>
                          Contact Customer:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {review.customerId.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Email fontSize="small" color="action" />
                              <Typography variant="body2" component="a" href={`mailto:${review.customerId.email}`} sx={{ textDecoration: 'none', color: 'primary.main' }}>
                                {review.customerId.email}
                              </Typography>
                            </Box>
                          )}
                          {review.customerId.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Phone fontSize="small" color="action" />
                              <Typography variant="body2" component="a" href={`tel:${review.customerId.phone}`} sx={{ textDecoration: 'none', color: 'primary.main' }}>
                                {review.customerId.phone}
                              </Typography>
                            </Box>
                          )}
                          {!review.customerId.email && !review.customerId.phone && (
                            <Typography variant="body2" color="text.secondary">
                              Contact information not available
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderRatingDialog;
